import React, { useState, useEffect, useRef } from 'react';
import { api } from '../App';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { toast } from 'sonner';
import { 
  UserPlus, 
  User, 
  Trash2, 
  Edit, 
  Upload, 
  FileText, 
  Star,
  Loader2,
  Eye,
  Camera,
  AlertCircle,
  Scan
} from 'lucide-react';
import PassportScanner from './PassportScanner';

const COUNTRIES = [
  { code: 'MA', name: 'Maroc' },
  { code: 'FR', name: 'France' },
  { code: 'ES', name: 'Espagne' },
  { code: 'DE', name: 'Allemagne' },
  { code: 'GB', name: 'Royaume-Uni' },
  { code: 'US', name: 'États-Unis' },
  { code: 'IT', name: 'Italie' },
  { code: 'BE', name: 'Belgique' },
  { code: 'CH', name: 'Suisse' },
  { code: 'NL', name: 'Pays-Bas' },
  { code: 'PT', name: 'Portugal' },
  { code: 'CA', name: 'Canada' },
  { code: 'OTHER', name: 'Autre' }
];

const DOC_TYPES = [
  { value: 'PASSPORT', label: 'Passeport' },
  { value: 'CIN', label: 'Carte d\'identité' },
  { value: 'OTHER', label: 'Autre' }
];

export default function PaxManager({ reservationId, onUpdate }) {
  const [paxList, setPaxList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [showScanner, setShowScanner] = useState(false);
  const [editingPax, setEditingPax] = useState(null);
  const [uploading, setUploading] = useState(null);
  const fileInputRef = useRef({});
  
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    birth_date: '',
    nationality: '',
    document_type: 'PASSPORT',
    document_number: '',
    is_main: false
  });
  const [passportFile, setPassportFile] = useState(null);
  const [passportPreview, setPassportPreview] = useState(null);

  useEffect(() => {
    if (reservationId) {
      fetchPax();
    }
  }, [reservationId]);

  const fetchPax = async () => {
    try {
      const response = await api.get(`/reservations/${reservationId}/pax`);
      setPaxList(response.data);
    } catch (error) {
      console.error('Failed to fetch pax:', error);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      first_name: '',
      last_name: '',
      birth_date: '',
      nationality: '',
      document_type: 'PASSPORT',
      document_number: '',
      is_main: paxList.length === 0 // First pax is main by default
    });
    setEditingPax(null);
    setPassportFile(null);
    setPassportPreview(null);
  };

  const openAddDialog = () => {
    resetForm();
    setShowDialog(true);
  };

  const openScanner = () => {
    resetForm();
    setShowScanner(true);
  };

  const handleScanComplete = (scannedData) => {
    // Use scanned data to pre-fill the form
    setFormData({
      first_name: scannedData.first_name || '',
      last_name: scannedData.last_name || '',
      birth_date: scannedData.birth_date || '',
      nationality: scannedData.nationality || '',
      document_type: scannedData.document_type || 'PASSPORT',
      document_number: scannedData.document_number || '',
      is_main: paxList.length === 0
    });
    
    // Set passport image if available
    if (scannedData.passportImage) {
      // Convert data URL to File
      fetch(scannedData.passportImage)
        .then(res => res.blob())
        .then(blob => {
          const file = new File([blob], 'passport-scan.jpg', { type: 'image/jpeg' });
          setPassportFile(file);
          setPassportPreview(scannedData.passportImage);
        });
    }
    
    setShowScanner(false);
    setShowDialog(true);
    toast.success('Données importées - Vérifiez et complétez si nécessaire');
  };

  const openEditDialog = (pax) => {
    setEditingPax(pax);
    setPassportFile(null);
    // Use passport_photo first, then document_file as fallback
    const existingPhoto = pax.passport_photo || pax.document_file;
    setPassportPreview(existingPhoto ? `${process.env.REACT_APP_BACKEND_URL}/api/uploads/${existingPhoto}` : null);
    setFormData({
      first_name: pax.first_name,
      last_name: pax.last_name,
      birth_date: pax.birth_date || '',
      nationality: pax.nationality || '',
      document_type: pax.document_type || 'PASSPORT',
      document_number: pax.document_number || '',
      is_main: pax.is_main
    });
    setShowDialog(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      let paxId = editingPax?.id;
      
      if (editingPax) {
        await api.put(`/reservations/${reservationId}/pax/${editingPax.id}`, formData);
        toast.success('Passager mis à jour');
      } else {
        const response = await api.post(`/reservations/${reservationId}/pax`, formData);
        paxId = response.data.id;
        toast.success('Passager ajouté');
      }
      
      // Upload passport photo if selected
      if (passportFile && paxId) {
        const photoData = new FormData();
        photoData.append('document', passportFile);
        try {
          await api.post(`/reservations/${reservationId}/pax/${paxId}/document`, photoData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
          toast.success('Photo passeport uploadée');
        } catch (uploadError) {
          toast.error('Erreur upload photo passeport');
        }
      }
      
      setShowDialog(false);
      resetForm();
      fetchPax();
      onUpdate?.();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erreur');
    }
  };

  const handlePassportFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Photo trop volumineuse (max 5MB)');
        return;
      }
      setPassportFile(file);
      const reader = new FileReader();
      reader.onloadend = () => setPassportPreview(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const handleDelete = async (paxId) => {
    if (!window.confirm('Supprimer ce passager ?')) return;
    try {
      await api.delete(`/reservations/${reservationId}/pax/${paxId}`);
      toast.success('Passager supprimé');
      fetchPax();
      onUpdate?.();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleFileUpload = async (paxId, file) => {
    if (!file) return;
    
    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Le fichier est trop volumineux (max 5MB)');
      return;
    }

    setUploading(paxId);
    const formData = new FormData();
    formData.append('document', file);

    try {
      await api.post(`/reservations/${reservationId}/pax/${paxId}/document`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Document uploadé');
      fetchPax();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erreur lors de l\'upload');
    } finally {
      setUploading(null);
    }
  };

  const triggerFileInput = (paxId) => {
    if (fileInputRef.current[paxId]) {
      fileInputRef.current[paxId].click();
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="w-6 h-6 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="pax-manager">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold">Passagers ({paxList.length})</h3>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={openScanner} data-testid="scan-passport-btn" className="border-primary text-primary hover:bg-primary/10">
            <Scan className="w-4 h-4 mr-1" />
            Scanner
          </Button>
          <Button size="sm" onClick={openAddDialog} data-testid="add-pax-btn">
            <UserPlus className="w-4 h-4 mr-1" />
            Manuel
          </Button>
        </div>
      </div>

      {paxList.length === 0 ? (
        <div className="text-center py-6 text-muted-foreground border-2 border-dashed rounded-lg">
          <User className="w-8 h-8 mx-auto mb-2 opacity-50" />
          <p>Aucun passager enregistré</p>
          <Button variant="link" onClick={openAddDialog}>
            Ajouter le premier passager
          </Button>
        </div>
      ) : (
        <div className="space-y-3">
          {paxList.map((pax) => (
            <div 
              key={pax.id} 
              className={`p-4 border rounded-xl transition-all ${pax.is_main ? 'border-primary bg-primary/5' : 'bg-gray-50'}`}
              data-testid={`pax-item-${pax.id}`}
            >
              <div className="flex gap-4">
                {/* Passport Photo Section */}
                <div className="flex-shrink-0">
                  {pax.passport_photo ? (
                    <div 
                      className="w-20 h-24 rounded-lg border-2 border-green-400 overflow-hidden cursor-pointer hover:border-green-600 transition-colors bg-white"
                      onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/uploads/${pax.passport_photo}`, '_blank')}
                      title="Cliquez pour agrandir"
                    >
                      <img 
                        src={`${process.env.REACT_APP_BACKEND_URL}/api/uploads/${pax.passport_photo}`}
                        alt={`Passeport ${pax.first_name}`}
                        className="w-full h-full object-cover"
                        onError={(e) => { 
                          e.target.style.display = 'none'; 
                          e.target.parentElement.classList.add('flex', 'items-center', 'justify-center');
                          e.target.parentElement.innerHTML = '<span class="text-xs text-gray-400">Erreur</span>';
                        }}
                      />
                    </div>
                  ) : pax.document_file ? (
                    <div 
                      className="w-20 h-24 rounded-lg border-2 border-blue-400 overflow-hidden cursor-pointer hover:border-blue-600 transition-colors bg-white"
                      onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/uploads/${pax.document_file}`, '_blank')}
                      title="Cliquez pour voir le document"
                    >
                      <img 
                        src={`${process.env.REACT_APP_BACKEND_URL}/api/uploads/${pax.document_file}`}
                        alt={`Document ${pax.first_name}`}
                        className="w-full h-full object-cover"
                        onError={(e) => { 
                          e.target.style.display = 'none';
                          e.target.parentElement.classList.add('flex', 'items-center', 'justify-center', 'bg-blue-50');
                          e.target.parentElement.innerHTML = '<span class="text-blue-600"><svg class="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path></svg></span>';
                        }}
                      />
                    </div>
                  ) : (
                    <div className="w-20 h-24 rounded-lg border-2 border-dashed border-amber-300 bg-amber-50 flex flex-col items-center justify-center">
                      <Camera className="w-6 h-6 text-amber-400" />
                      <span className="text-xs text-amber-500 mt-1">Photo</span>
                    </div>
                  )}
                </div>
                
                {/* Pax Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-semibold text-lg">{pax.first_name} {pax.last_name}</span>
                    {pax.is_main && (
                      <Badge variant="default" className="text-xs">
                        <Star className="w-3 h-3 mr-1" />
                        Principal
                      </Badge>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm text-muted-foreground">
                    {pax.nationality && (
                      <span>{COUNTRIES.find(c => c.code === pax.nationality)?.name || pax.nationality}</span>
                    )}
                    {pax.birth_date && (
                      <span>Né(e) le {new Date(pax.birth_date).toLocaleDateString('fr-FR')}</span>
                    )}
                    {pax.document_number && (
                      <span className="font-mono">{DOC_TYPES.find(d => d.value === pax.document_type)?.label}: {pax.document_number}</span>
                    )}
                  </div>
                  
                  {/* Document status badges */}
                  <div className="flex gap-2 mt-2">
                    {pax.passport_photo ? (
                      <Badge variant="outline" className="text-xs bg-green-50 text-green-700 border-green-300">
                        <Camera className="w-3 h-3 mr-1" />
                        Photo OK
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="text-xs bg-amber-50 text-amber-700 border-amber-300">
                        <AlertCircle className="w-3 h-3 mr-1" />
                        Photo manquante
                      </Badge>
                    )}
                    {pax.document_file && !pax.passport_photo && (
                      <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700 border-blue-300">
                        <FileText className="w-3 h-3 mr-1" />
                        Document
                      </Badge>
                    )}
                  </div>
                </div>
                
                {/* Actions */}
                <div className="flex flex-col gap-1">
                  {/* View photo/document */}
                  {(pax.passport_photo || pax.document_file) && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="text-green-600"
                      onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/uploads/${pax.passport_photo || pax.document_file}`, '_blank')}
                      title="Voir le document"
                    >
                      <Eye className="w-4 h-4" />
                    </Button>
                  )}
                  
                  <input
                    type="file"
                    accept="image/*,.pdf"
                    className="hidden"
                    ref={el => fileInputRef.current[pax.id] = el}
                    onChange={(e) => handleFileUpload(pax.id, e.target.files[0])}
                  />
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => triggerFileInput(pax.id)}
                    disabled={uploading === pax.id}
                    title={pax.passport_photo || pax.document_file ? 'Remplacer' : 'Ajouter photo'}
                    className={!pax.passport_photo && !pax.document_file ? 'text-amber-600' : ''}
                  >
                    {uploading === pax.id ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Upload className="w-4 h-4" />
                    )}
                  </Button>
                  
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => openEditDialog(pax)}
                  >
                    <Edit className="w-4 h-4" />
                  </Button>
                  
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => handleDelete(pax.id)}
                    className="text-red-500 hover:text-red-700"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingPax ? 'Modifier le passager' : 'Ajouter un passager'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Prénom *</Label>
                <Input
                  value={formData.first_name}
                  onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                  required
                  data-testid="pax-first-name"
                />
              </div>
              <div>
                <Label>Nom *</Label>
                <Input
                  value={formData.last_name}
                  onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                  required
                  data-testid="pax-last-name"
                />
              </div>
              <div>
                <Label>Date de naissance</Label>
                <Input
                  type="date"
                  value={formData.birth_date}
                  onChange={(e) => setFormData({ ...formData, birth_date: e.target.value })}
                />
              </div>
              <div>
                <Label>Nationalité</Label>
                <Select 
                  value={formData.nationality} 
                  onValueChange={(v) => setFormData({ ...formData, nationality: v })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Sélectionner..." />
                  </SelectTrigger>
                  <SelectContent>
                    {COUNTRIES.map((c) => (
                      <SelectItem key={c.code} value={c.code}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Type de document</Label>
                <Select 
                  value={formData.document_type} 
                  onValueChange={(v) => setFormData({ ...formData, document_type: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {DOC_TYPES.map((d) => (
                      <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>N° de document</Label>
                <Input
                  value={formData.document_number}
                  onChange={(e) => setFormData({ ...formData, document_number: e.target.value })}
                  placeholder="Ex: AB123456"
                />
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="is_main"
                checked={formData.is_main}
                onChange={(e) => setFormData({ ...formData, is_main: e.target.checked })}
                className="rounded"
              />
              <Label htmlFor="is_main" className="cursor-pointer">
                Passager principal (titulaire de la réservation)
              </Label>
            </div>

            {/* Passport photo upload */}
            <div className="border-2 border-dashed border-amber-300 rounded-lg p-4 bg-amber-50">
              <Label className="flex items-center gap-2 text-amber-800 font-medium mb-3">
                <Camera className="w-4 h-4" />
                Photo du passeport (obligatoire pour la fiche de police)
              </Label>
              <div className="flex items-center gap-4">
                {passportPreview ? (
                  <div className="relative w-32 h-24 rounded-lg overflow-hidden border-2 border-amber-400">
                    <img src={passportPreview} alt="Passeport" className="w-full h-full object-cover" />
                    <button
                      type="button"
                      className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded-full text-xs"
                      onClick={() => { setPassportFile(null); setPassportPreview(null); }}
                    >
                      ×
                    </button>
                  </div>
                ) : (
                  <div className="w-32 h-24 border-2 border-dashed border-gray-300 rounded-lg flex flex-col items-center justify-center text-gray-400">
                    <FileText className="w-8 h-8 mb-1" />
                    <span className="text-xs">Aucune photo</span>
                  </div>
                )}
                <div className="flex-1">
                  <input
                    type="file"
                    accept="image/*"
                    capture="environment"
                    onChange={handlePassportFileChange}
                    className="hidden"
                    id="passport-photo-input"
                  />
                  <label htmlFor="passport-photo-input">
                    <Button type="button" variant="outline" size="sm" asChild className="border-amber-400 text-amber-700 hover:bg-amber-100">
                      <span>
                        <Camera className="w-4 h-4 mr-2" />
                        {passportPreview ? 'Changer la photo' : 'Prendre / Choisir photo'}
                      </span>
                    </Button>
                  </label>
                  <p className="text-xs text-muted-foreground mt-1">
                    Photographiez la page d'identité du passeport
                  </p>
                </div>
              </div>
              {!passportPreview && (
                <div className="flex items-center gap-2 mt-2 text-amber-700 text-sm">
                  <AlertCircle className="w-4 h-4" />
                  <span>Sans photo, la fiche de police sera incomplète</span>
                </div>
              )}
            </div>
            
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowDialog(false)}>
                Annuler
              </Button>
              <Button type="submit" data-testid="save-pax-btn">
                {editingPax ? 'Mettre à jour' : 'Ajouter'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Passport Scanner Dialog */}
      <Dialog open={showScanner} onOpenChange={setShowScanner}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Scan className="w-5 h-5 text-primary" />
              Scanner un passeport
            </DialogTitle>
          </DialogHeader>
          <PassportScanner 
            onScanComplete={handleScanComplete}
            onCancel={() => setShowScanner(false)}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}
